export type UserRole = 'admin' | 'editor' | 'author' | 'viewer';

export interface RolePermissions {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
  canManageUsers: boolean;
  canViewActivityLogs: boolean;
}

/**
 * Get role permissions based on user role
 */
export const getRolePermissions = (role: UserRole | null | undefined): RolePermissions => {
  if (!role) {
    return {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canView: false,
      canManageUsers: false,
      canViewActivityLogs: false,
    };
  }

  switch (role) {
    case 'admin':
      return {
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        canManageUsers: true,
        canViewActivityLogs: true,
      };
    case 'editor':
      return {
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        canManageUsers: false, // Can view but not manage
        canViewActivityLogs: true,
      };
    case 'author':
      return {
        canCreate: true,
        canEdit: true,
        canDelete: false,
        canView: true,
        canManageUsers: false,
        canViewActivityLogs: false,
      };
    case 'viewer':
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: true,
        canManageUsers: false,
        canViewActivityLogs: false,
      };
    default:
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: false,
        canManageUsers: false,
        canViewActivityLogs: false,
      };
  }
};

/**
 * Check if user has required role(s)
 */
export const hasRole = (userRole: UserRole | null | undefined, requiredRoles: UserRole | UserRole[]): boolean => {
  if (!userRole) return false;
  
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  return roles.includes(userRole);
};

/**
 * Check if user can access a route based on role
 */
export const canAccessRoute = (userRole: UserRole | null | undefined, route: string): boolean => {
  if (!userRole) return false;

  // Admin can access everything
  if (userRole === 'admin') return true;

  // Users management routes - only admin and editor can view, only admin can create/edit
  if (route.startsWith('/users')) {
    if (route === '/users/list' || route === '/users') {
      return hasRole(userRole, ['admin', 'editor']);
    }
    if (route === '/users/create' || route.includes('/edit')) {
      return hasRole(userRole, ['admin']);
    }
  }

  // Activity logs - only admin and editor
  if (route.startsWith('/activity-logs')) {
    return hasRole(userRole, ['admin', 'editor']);
  }

  // Content creation routes - admin, editor, author
  if (route.includes('/create') || route.includes('/edit')) {
    return hasRole(userRole, ['admin', 'editor', 'author']);
  }

  // All other routes - all authenticated users can view
  return true;
};

